import React, { useState, useRef, useEffect } from 'react';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  SkipBack, SkipForward, Loader2, AlertCircle, RefreshCw, ExternalLink
} from 'lucide-react';

const VideoPlayer = ({ videoUrl, embedUrl, thumbnail, title, onProgress, autoPlay = false }) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Construct embed URL if not provided but we have video ID
  const getEmbedUrl = () => {
    if (embedUrl && embedUrl !== '' && embedUrl !== '#') {
      return embedUrl;
    }
    // Try to extract video ID from URL and construct embed
    if (videoUrl && videoUrl.includes('viewkey=')) {
      const match = videoUrl.match(/viewkey=([a-zA-Z0-9]+)/);
      if (match) {
        return `https://www.pornhub.com/embed/${match[1]}`;
      }
    }
    return null;
  };

  const finalEmbedUrl = getEmbedUrl();

  // Get direct source URL for opening in new tab
  const getSourceUrl = () => {
    if (videoUrl && videoUrl !== '#') return videoUrl;
    if (finalEmbedUrl) {
      // Convert embed URL to watch URL
      const match = finalEmbedUrl.match(/embed\/([a-zA-Z0-9]+)/);
      if (match) {
        return `https://www.pornhub.com/view_video.php?viewkey=${match[1]}`;
      }
    }
    return null;
  };

  const sourceUrl = getSourceUrl();

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      const elem = containerRef.current;
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        }
      }
    } catch (err) {
      console.log('Fullscreen error:', err);
    }
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const retryLoad = () => {
    setIsLoading(true);
    setHasError(false);
    if (iframeRef.current) {
      iframeRef.current.src = finalEmbedUrl;
    }
  };

  const openInSource = () => {
    if (sourceUrl) {
      window.open(sourceUrl, '_blank');
    }
  };

  // If we have an embed URL, show the embedded player
  if (finalEmbedUrl) {
    return (
      <div 
        ref={containerRef}
        className="relative w-full bg-black rounded-lg overflow-hidden"
        data-testid="video-player"
      >
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 z-10 bg-black flex items-center justify-center aspect-video">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-white mx-auto mb-4" />
              <p className="text-gray-400">Loading video...</p>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {hasError && (
          <div className="absolute inset-0 z-10 bg-black/90 flex items-center justify-center aspect-video">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-white mb-2">Video failed to load</p>
              <p className="text-gray-500 text-sm mb-4">The embedded player may be blocked</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={retryLoad}
                  className="px-4 py-2 bg-white/20 text-white rounded hover:bg-white/30 transition-colors inline-flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retry
                </button>
                {sourceUrl && (
                  <button
                    onClick={openInSource}
                    className="px-4 py-2 bg-white text-black rounded hover:bg-gray-200 transition-colors inline-flex items-center gap-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Watch on Source
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Thumbnail background while loading */}
        {isLoading && thumbnail && (
          <img 
            src={thumbnail} 
            alt={title}
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
        )}

        {/* Embedded iframe player - Full control to iframe */}
        <div className="aspect-video">
          <iframe
            ref={iframeRef}
            src={finalEmbedUrl}
            className="w-full h-full"
            frameBorder="0"
            allowFullScreen={true}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            title={title}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            style={{ border: 'none' }}
          />
        </div>

        {/* Minimal bottom bar - only shows when not fullscreen */}
        {!isFullscreen && (
          <div className="bg-[#0A0A0A] px-4 py-3 flex items-center justify-between border-t border-white/5">
            <h3 className="text-white text-sm font-medium truncate flex-1 mr-4">{title}</h3>
            <div className="flex items-center gap-2">
              {sourceUrl && (
                <button
                  onClick={openInSource}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  title="Open in source site"
                  data-testid="open-source-btn"
                >
                  <ExternalLink className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={toggleFullscreen}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                title="Toggle fullscreen"
                data-testid="fullscreen-btn"
              >
                {isFullscreen ? (
                  <Minimize className="w-5 h-5" />
                ) : (
                  <Maximize className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Tip for users */}
        <div className="bg-[#121212] px-4 py-2 text-xs text-gray-500 flex items-center justify-center gap-2">
          <span>💡 Use the player controls inside the video for playback. Click fullscreen icon in player for best experience.</span>
        </div>
      </div>
    );
  }

  // Fallback: Show thumbnail with play button that opens in new tab
  return (
    <div 
      ref={containerRef}
      className="relative w-full aspect-video bg-black rounded-lg overflow-hidden group cursor-pointer"
      onClick={() => videoUrl && videoUrl !== '#' && window.open(videoUrl, '_blank')}
      data-testid="video-player-fallback"
    >
      {/* Thumbnail */}
      <img 
        src={thumbnail || 'https://images.unsplash.com/photo-1721040530907-646692053af8?w=1280'} 
        alt={title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
      />
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/50 group-hover:bg-black/60 transition-colors" />
      
      {/* Play button */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 group-hover:bg-white/30 transition-colors">
          <Play className="w-10 h-10 text-white fill-white ml-1" />
        </div>
        <p className="text-white text-lg font-medium">Click to Watch</p>
        <p className="text-gray-400 text-sm mt-1">Opens in source player</p>
      </div>

      {/* Title bar */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
        <h3 className="text-white font-medium truncate">{title}</h3>
      </div>
    </div>
  );
};

export default VideoPlayer;
